/**
 * src/modules/orders-list/bulk-actions.ts — Szybkie akcje masowe na liście zamówień
 * (2026-07-24, plan „szybkie akcje w zamówieniach").
 *
 * Etap 1: wspólny runner z limitem współbieżności + raport per zamówienie (modal),
 * przepisana zmiana statusu (wcześniej inline w toolbar.ts bez raportu) oraz
 * bulk notatka (DOPISYWANA jako komentarz z autorem i datą — nie nadpisuje niczego).
 *
 * Zasada: każda akcja masowa kończy się raportem ok/błąd per zamówienie —
 * nigdy „all-or-nothing" bez informacji, które zamówienia poległy.
 */

import { $ordersSelectedIds } from '../../stores/orders.js';
import { orderUrl } from '../order-ref.ts';
import { formatLocalDateTimePL } from '../_datetime.ts';
import { zapamietajWidok } from '../plynne.ts';
import { skutekWKanale } from './skutek-w-kanale.ts';
import { etykietaDostepna, przesylkaNieudana } from '../orders/etykieta-dostepna.ts';
import type { Shipment } from '../../types/dto.ts';

// AUDYT 2026-08-11: po mutacji lista MUSI dostac SWIEZE dane — render ze starego
// store'u + cache strony (sessionStorage, TTL 60 s) pokazywal stan sprzed zmiany
// az do F5. Inwalidacja cache + wymuszony refetch (force omija juz sessionStorage).
function _odswiezListeZamowienPoMutacji() {
  try {
    var inv: any = (window as any)._invalidateOrdersPageCache;
    if (typeof inv === 'function') inv();
  } catch (_e) {}
  try {
    var ld: any = (window as any).loadAllegroOrdersIntoPanel
      || ((window as any).SellBaseApp || {}).loadAllegroOrdersIntoPanel;
    // 19.10.2026 (właściciel): przeniesione / zmienione zamówienia znikają z widoku, a lista przerysowuje się
    // w całości — przewinięcie strony zostaje tam, gdzie człowiek pracował (bez skoku na dół albo na górę).
    var przywrocWidok = zapamietajWidok(document.getElementById('orders-list-container'));
    if (typeof ld === 'function') Promise.resolve(ld(true)).then(przywrocWidok, przywrocWidok);
  } catch (_e) {}
  // Liczniki w pasku statusów mają własny cache (klient 60 s + serwer 60 s) — po
  // mutacji trzeba je zrzucić razem z listą, inaczej pasek pokazuje grupę z jednym
  // zamówieniem, a lista pod nią jest pusta (zgłoszenie usera 2026-08-21).
  try { (window as any).__sbOdswiezStatsZamowien && (window as any).__sbOdswiezStatsZamowien(true); } catch (_e) { /* liczniki odświeżą się przy następnym ticku */ }
}


// State proxy (legacy `window.__sbState`)
var state: any = (typeof window !== 'undefined' && (window as any).__sbState) || (function () {
  var _p = new Proxy({}, {
    get: function (_, k: any) { return (window as any).__sbState ? ((window as any).__sbState as any)[k] : undefined; },
    set: function (_, k: any, v: any) { if ((window as any).__sbState) ((window as any).__sbState as any)[k] = v; return true; },
  });
  return _p;
})();

// Lazy delegates (cross-module, wzorzec jak toolbar.ts)
function showToast(this: any, ...args: any[]) { return ((((window as any).__sbExports && (window as any).__sbExports.showToast) || (window as any).showToast) as any).apply(this, args); }
function openModal(this: any, ...args: any[]) { return ((((window as any).__sbExports && (window as any).__sbExports.openModal) || (window as any).openModal) as any).apply(this, args); }
function closeModal(this: any, ...args: any[]) { return ((((window as any).__sbExports && (window as any).__sbExports.closeModal) || (window as any).closeModal) as any).apply(this, args); }
function escapeHtml(this: any, ...args: any[]) { return ((((window as any).__sbExports && (window as any).__sbExports.escapeHtml) || (window as any).escapeHtml) as any).apply(this, args); }
function getOrderApiId(this: any, ...args: any[]) { return ((((window as any).__sbExports && (window as any).__sbExports.getOrderApiId) || (window as any).getOrderApiId) as any).apply(this, args); }
function statusColorForLabel(this: any, ...args: any[]) { return ((((window as any).__sbExports && (window as any).__sbExports.statusColorForLabel) || (window as any).statusColorForLabel) as any).apply(this, args); }
function upsertOrderInStore(this: any, ...args: any[]) { return ((((window as any).__sbExports && (window as any).__sbExports.upsertOrderInStore) || (window as any).upsertOrderInStore) as any).apply(this, args); }
function _updateBulkBar(this: any, ...args: any[]) { return ((((window as any).__sbExports && (window as any).__sbExports._updateBulkBar) || (window as any)._updateBulkBar) as any).apply(this, args); }
function renderOrdersView(this: any, ...args: any[]) { return ((((window as any).__sbExports && (window as any).__sbExports.renderOrdersView) || (window as any).renderOrdersView) as any).apply(this, args); }

function _api(): any { return (window as any).SellBaseAPI; }
function _data(): any { return (window as any).SellBaseData; }

export interface BulkResult {
  id: string;
  label: string;
  ok: boolean;
  error?: string;
  info?: string;
  /** S-210 (B): zmiana w SellBase się udała, ale kanał jej nie dostał (odmowa, wyłącznik, brak odpowiednika). */
  uwaga?: string;
  /** S-210 (przegląd): `uwaga` to odmowa kanału (czerwona), a nie świadome pominięcie (żółte). */
  odmowa?: boolean;
}


// Etykieta zamówienia do raportu — numer/ID + kupujący (czytelne dla usera).
// 2026-08-21 (zgłoszenie usera: „zaznaczyłem 200 wg filtrów i dałem zmień status,
// w czym tu problem"). Akcje zbiorcze wymagały PEŁNEGO wiersza zamówienia z pamięci
// przeglądarki, a w niej siedzi wyłącznie bieżąca strona — więc zaznaczenie ponad
// stronę kończyło się „nie znaleziono danych zamówienia" dla wszystkiego spoza niej.
//
// Wymóg był sztuczny: getOrderApiId to w istocie przekształcenie samego napisu
// (id złożone zostaje, prefiks AL- odpada, reszta na małe litery). Wiersz służył
// tam jedynie jako źródło zapasowe identyfikatora oraz do kosmetyki: odświeżenia
// statusu w pamięci i etykiety w raporcie. Nic z tego nie jest potrzebne, żeby
// zmienić status zamówienia, którego identyfikator już mamy.
function _apiIdZIdentyfikatora(orderId: any): string {
  return String(getOrderApiId({ id: orderId }) || '');
}

export function _orderLabel(order: any): string {
  // 2026-09-04: nasze ID (public_id) ma pierwszenstwo
  if (order && order.short_no != null && /^\d+$/.test(String(order.short_no))) return '#' + String(order.short_no);
  if (order && /^[0-9A-HJKMNP-TV-Z]{26}$/i.test(String(order.public_id || ''))) return '#' + String(order.public_id).toUpperCase();
  if (!order) return '';
  var num = order.order_number || order.id || '';
  var buyer = order.buyer_login || (order.buyer && order.buyer.login) || order.customerName || '';
  return String(num) + (buyer ? ' · ' + String(buyer) : '');
}

// Wspólny runner: przetwarza `ids` workerem z ograniczoną współbieżnością (default 3 —
// nie zalewamy backendu ani rate-limitów marketplace), pokazuje postęp na przycisku.
export async function _runBulkOverOrders(
  ids: string[],
  worker: (orderId: string) => Promise<string | void | { label?: string; info?: string; uwaga?: string; odmowa?: boolean }>,
  opts?: { concurrency?: number; progressBtn?: HTMLElement | null; progressLabel?: string },
): Promise<BulkResult[]> {
  var results: BulkResult[] = new Array(ids.length);
  var idx = 0;
  var done = 0;
  var btn: any = (opts && opts.progressBtn) || null;
  var origHtml = btn ? btn.innerHTML : '';
  if (btn) btn.disabled = true;

  async function _lane(): Promise<void> {
    while (idx < ids.length) {
      var i = idx++;
      var id = ids[i];
      try {
        var wynik = await worker(id);
        if (wynik && typeof wynik === 'object') {
          results[i] = { id: id, label: wynik.label || id, ok: true, info: wynik.info, uwaga: wynik.uwaga, odmowa: wynik.odmowa };
        } else {
          results[i] = { id: id, label: (wynik as string) || id, ok: true };
        }
      } catch (e: any) {
        var order = _data() && _data().getOrderById ? _data().getOrderById(id) : null;
        results[i] = { id: id, label: _orderLabel(order) || id, ok: false, error: (e && e.message) || String(e) };
      }
      done++;
      if (btn) btn.textContent = ((opts && opts.progressLabel) || 'Przetwarzam') + '… ' + done + '/' + ids.length;
    }
  }

  var lanes = Math.max(1, Math.min((opts && opts.concurrency) || 3, ids.length));
  var laneArr: Promise<void>[] = [];
  for (var l = 0; l < lanes; l++) laneArr.push(_lane());
  await Promise.all(laneArr);

  if (btn) {
    btn.disabled = false;
    btn.innerHTML = origHtml;
    try { if ((window as any).lucide && (window as any).lucide.createIcons) (window as any).lucide.createIcons({ nodes: [btn] }); } catch (_) {}
  }
  return results;
}

// Adres zamówienia do raportu: numer SellBase / public_id z wiersza listy, a gdy wiersza nie ma
// w pamięci (zamówienie spoza bieżącej strony) — surowy identyfikator, który router też rozwiąże.
function _adresZamowieniaRaportu(id: string): string {
  var order = null;
  try { order = _data() && _data().getOrderById ? _data().getOrderById(id) : null; } catch (_) { order = null; }
  if (order) return orderUrl(order);
  return '/orders/' + encodeURIComponent(String(id || '').replace(/^AL-/i, ''));
}

// Raport po akcji masowej — modal z listą ✔/✖ per zamówienie.
// 2026-09-15 (właściciel): numer zamówienia w każdym wierszu jest linkiem otwierającym
// zamówienie w NOWEJ karcie — po szybkim pakowaniu da się sprawdzić każde z nich bez
// zamykania podsumowania.
export function _showBulkReport(title: string, results: BulkResult[], opts?: { note?: string }) {
  var failN = results.filter(function (r) { return r && !r.ok; }).length;
  var pomN = results.filter(function (r) { return r && r.ok && r.uwaga && !r.odmowa; }).length;
  var odmN = results.filter(function (r) { return r && r.ok && r.uwaga && r.odmowa; }).length;
  var okN = results.filter(function (r) { return r && r.ok && !r.uwaga; }).length;
  var html = '<div style="display:flex;flex-wrap:wrap;gap:6px 16px;margin-bottom:10px;font-size:13px;font-weight:600">' +
    '<span style="color:var(--color-success,#12b76a)">✔ Udane: ' + okN + '</span>' +
    (pomN ? '<span style="color:var(--color-warning-500,#f79009)">⚠ Tylko w SellBase: ' + pomN + '</span>' : '') +
    (odmN ? '<span style="color:var(--color-error-500,#f04438)">⚠ Kanał odmówił: ' + odmN + '</span>' : '') +
    (failN ? '<span style="color:var(--color-error-500,#f04438)">✖ Nieudane: ' + failN + '</span>' : '') +
    '</div>' +
    '<div style="max-height:300px;overflow-y:auto;display:flex;flex-direction:column;gap:4px">';
  results.forEach(function (r) {
    if (!r) return;
    var zUwaga = !!(r.ok && r.uwaga);
    var zOdmowa = zUwaga && !!r.odmowa;
    html += '<div style="display:flex;gap:8px;align-items:baseline;font-size:12px;padding:5px 8px;border-radius:6px;background:' +
      (zOdmowa ? 'rgba(240,68,56,.08)' : zUwaga ? 'var(--color-warning-bg,rgba(247,144,9,.12))' : r.ok ? 'var(--color-primary-subtle,rgba(70,95,255,.06))' : 'rgba(240,68,56,.08)') + '">' +
      '<span style="color:' + (zOdmowa ? 'var(--color-error-500,#f04438)' : zUwaga ? 'var(--color-warning-500,#f79009)' : r.ok ? 'var(--color-success,#12b76a)' : 'var(--color-error-500,#f04438)') + ';flex-shrink:0">' + (zUwaga ? '⚠' : r.ok ? '✔' : '✖') + '</span>' +
      '<span style="min-width:0;overflow:hidden;text-overflow:ellipsis">' + _etykietaZLinkiem(r) + '</span>' +
      (r.info ? '<span style="color:var(--color-text-muted);font-size:11px">' + escapeHtml(r.info) + '</span>' : '') +
      (zUwaga ? '<span style="color:' + (zOdmowa ? 'var(--color-error-500,#f04438)' : 'var(--color-warning-700,#b54708)') + ';font-size:11px">— ' + escapeHtml(String(r.uwaga)) + '</span>' : '') +
      (r.error ? '<span style="color:var(--color-error-500,#f04438);font-size:11px">— ' + escapeHtml(r.error) + '</span>' : '') +
      '</div>';
  });
  html += '</div>';
  if (opts && opts.note) {
    html += '<div style="margin-top:10px;font-size:11.5px;color:var(--color-text-muted);line-height:1.5">' + opts.note + '</div>';
  }
  html += '<div style="margin-top:8px;font-size:11px;color:var(--color-text-faint)">Kliknięcie numeru zamówienia otwiera je w nowej karcie.</div>';
  openModal(title, html, '<button class="btn-primary" id="bulk-report-close">OK</button>');
  var closeBtn = document.getElementById('bulk-report-close');
  if (closeBtn) closeBtn.addEventListener('click', function () { closeModal(); });
}

function _etykietaZLinkiem(r: BulkResult): string {
  var pelna = String(r.label || r.id || '');
  var czesci = pelna.split(' → ');
  var numer = czesci[0] || pelna;
  var reszta = czesci.length > 1 ? ' → ' + czesci.slice(1).join(' → ') : '';
  var href = _adresZamowieniaRaportu(r.id);
  return '<a href="' + escapeHtml(href) + '" target="_blank" rel="noopener" title="Otwórz zamówienie w nowej karcie" ' +
    'style="font-weight:600;color:var(--color-primary);text-decoration:none;white-space:nowrap">' + escapeHtml(numer) + ' <span aria-hidden="true">↗</span></a>' +
    escapeHtml(reszta);
}

function _selectedIds(): string[] {
  return Array.from($ordersSelectedIds.get()) as string[];
}

function _afterBulk(results: BulkResult[]) {
  // Pełny sukces → czyścimy selekcję (jak dotychczasowy flow); częściowy → selekcja
  // zostaje, żeby user mógł ponowić dla nieudanych. S-210: odmowa kanału też zostawia selekcję.
  var allOk = results.length > 0 && results.every(function (r) { return r && r.ok && !r.odmowa; });
  if (allOk) {
    try { (state as any).ordersSelectedIds.clear(); } catch (_) {}
    try { _updateBulkBar(); } catch (_) {}
  }
  // Refetch zamiast renderu starego store'u: etykiety/statusy/tagi zmienily dane
  // na backendzie, a store i cache strony wciaz trzymaly stan sprzed mutacji.
  _odswiezListeZamowienPoMutacji();
}

// ── Zmiana statusu (przepisane z toolbar.ts: + współbieżność, + raport) ──
async function _applyBulkStatus() {
  var sel: any = document.getElementById('bulk-status-select');
  var newStatus = sel ? sel.value : '';
  if (!newStatus) { showToast('Wybierz status', 'warning'); return; }
  var ids = _selectedIds();
  if (!ids.length) return;
  var btn = document.getElementById('bulk-apply-status');
  var results = await _runBulkOverOrders(ids, async function (orderId) {
    var order = _data().getOrderById(orderId);
    // Wiersz jest MILE WIDZIANY, ale nie wymagany — patrz `_apiIdZIdentyfikatora`.
    var apiId = order ? getOrderApiId(order) : _apiIdZIdentyfikatora(orderId);
    if (!apiId) throw new Error('brak identyfikatora API');
    var odp = await _api().updateOrderStatus(apiId, newStatus);
    // ETAP 3 (2026-08-10): KLUCZ, nie slug nazwy — ta sama poprawka co w `orders-shared.ts`.
    // Bez niej zamówienie tuż po zmianie statusu miało w store klucz `zapakowane`
    // i wypadało z własnego filtra do najbliższego odświeżenia listy.
    var _kl = (window as any).__sbExports && (window as any).__sbExports.kluczStatusu;
    if (order) {
      // Kosmetyka: zamówienie widoczne na ekranie od razu pokazuje nowy status.
      // Dla zamówień spoza bieżącej strony nie ma czego odświeżać — zobaczy je
      // przy najbliższym przeładowaniu listy.
      order.status = {
        key: _kl ? String(_kl(newStatus) || '') : newStatus.toLowerCase().replace(/\s+/g, '_'),
        label: newStatus, color: statusColorForLabel(newStatus),
      };
      upsertOrderInStore(order);
    }
    try { (window as any).__sbBroadcast?.broadcast('order:updated', { id: apiId, status: newStatus }); } catch (_) {}
    var skutek = skutekWKanale(odp, order && (order.source || order.marketplace));
    return { label: order ? _orderLabel(order) : String(orderId), info: skutek.info, uwaga: skutek.uwaga, odmowa: skutek.odmowa };
  }, { progressBtn: btn, progressLabel: 'Zmieniam' });
  // Tytuł idzie przez textContent w openModal — bez escapeHtml (podwójne encje w UI).
  _showBulkReport('Zmiana statusu → „' + newStatus + '”', results, {
    note: 'Przy każdym zamówieniu: „wysłane do: …” — status poszedł do marketplace. Żółte ⚠ — status zmieniony tylko w SellBase '
      + '(wysyłanie wyłączone, brak odpowiednika, kanał nie przyjmuje statusu). Czerwone ⚠ — kanał odmówił: Allegro, Erli i '
      + 'WooCommerce SellBase ponowi sam (do 5 razy w ciągu ok. 2 dni); pozostałe popraw w panelu kanału albo ponów.',
  });
  _afterBulk(results);
}

// ── Bulk notatka (dopisywana jako komentarz — autor + data, widoczna w szczegółach) ──
function _openBulkNoteModal() {
  var ids = _selectedIds();
  if (!ids.length) { showToast('Nie zaznaczono żadnego zamówienia', 'warning'); return; }
  openModal(
    'Dodaj notatkę — ' + ids.length + ' zamówień',
    '<textarea id="bulk-note-text" rows="4" class="form-input" style="width:100%;resize:vertical" placeholder="Treść notatki…"></textarea>' +
    '<div style="margin-top:8px;font-size:11.5px;color:var(--color-text-muted);line-height:1.5">' +
    'Notatka <b>dopisze się</b> do każdego z zaznaczonych zamówień jako komentarz (z autorem i datą), ' +
    'widoczny w szczegółach zamówienia. Istniejące notatki i komentarze zostają.</div>',
    '<button class="btn-secondary" id="bulk-note-cancel">Anuluj</button>' +
    '<button class="btn-primary" id="bulk-note-save">Dodaj (' + ids.length + ')</button>'
  );
  var cancelBtn = document.getElementById('bulk-note-cancel');
  if (cancelBtn) cancelBtn.addEventListener('click', function () { closeModal(); });
  var saveBtn: any = document.getElementById('bulk-note-save');
  if (saveBtn) saveBtn.addEventListener('click', async function () {
    var ta: any = document.getElementById('bulk-note-text');
    var text = (ta && ta.value || '').trim();
    if (!text) { showToast('Wpisz treść notatki', 'warning'); if (ta) ta.focus(); return; }
    saveBtn.disabled = true;
    var results = await _runBulkOverOrders(ids, async function (orderId) {
      var order = _data().getOrderById(orderId);
      var apiId = order ? getOrderApiId(order) : _apiIdZIdentyfikatora(orderId);
      if (!apiId) throw new Error('brak identyfikatora API');
      await _api().addOrderComment(apiId, text);
      return order ? _orderLabel(order) : String(orderId);
    }, { progressBtn: saveBtn, progressLabel: 'Dodaję' });
    closeModal();
    _showBulkReport('Notatka dodana', results);
    _afterBulk(results);
  });
}

// ── 0232: Przenieś do grupy ROBOCZEJ (kubełki) ────────────────────────────────
// Grupa robocza = szuflada na przerzucenia (ręczne albo z reguły). Zamówienie może
// być najwyżej w JEDNEJ — przeniesienie zabiera z poprzedniej, a na czas pobytu
// zamówienie znika z grup automatycznych (pierwszeństwo roboczej).
/** Grupy ROBOCZE firmy (manual) — jedno źródło dla okna i paska masowego. */
async function _grupyRobocze(): Promise<any[]> {
  var r = await fetch('/api/order-status-groups', { headers: _authHeaders() });
  var d = await r.json();
  if (!r.ok) throw new Error((d && d.detail) || ('HTTP ' + r.status));
  return ((d && d.groups) || []).filter(function (g: any) {
    return String(g.group_type || 'auto') === 'manual';
  });
}

/**
 * Przypisanie grupy roboczej zaznaczonym zamówieniom — wspólne dla okna
 * i dla listy w pasku masowym (2026-08-25, zgłoszenie usera: grupa ma być
 * pierwsza w pasku, obok statusu i tagu, a nie schowana w oknie).
 *
 * `groupId === null` = zdejmij z grupy roboczej.
 */
async function _przypiszGrupeZaznaczonym(ids: string[], groupId: number | null,
                                         nazwa: string): Promise<void> {
  var r2 = await fetch('/api/order-manual-groups/assign-bulk', {
    method: 'POST', headers: _authHeaders(),
    body: JSON.stringify({ order_ids: ids, group_id: groupId }),
  });
  var d2 = await r2.json();
  if (!r2.ok) throw new Error((d2 && d2.detail) || ('HTTP ' + r2.status));
  var czesci = ['przeniesione: ' + (d2.changed || 0)];
  if (d2.errors && d2.errors.length) czesci.push('błędy: ' + d2.errors.length);
  showToast((groupId ? 'Grupa „' + nazwa + '" — ' : 'Zdjęto z grup roboczych — ') + czesci.join(', '),
    (d2.errors && d2.errors.length) ? 'info' : 'success');
  try { (state as any).ordersSelectedIds.clear(); } catch (_) {}
  try { _updateBulkBar(); } catch (_) {}
  // Liczniki grup w pasku bocznym liczą z serwera — odśwież po mutacji.
  _odswiezListeZamowienPoMutacji();
}

/** Wypełnia listę grup w pasku masowym (raz na sesję listy). */
export async function _wypelnijGrupyWPasku(): Promise<void> {
  var sel: any = document.getElementById('bulk-group-select-inline');
  if (!sel || sel.options.length > 1) return;
  try {
    var grupy = await _grupyRobocze();
    grupy.forEach(function (g: any) {
      var opt = document.createElement('option');
      opt.value = String(g.id);
      opt.textContent = String(g.name || '');
      sel.appendChild(opt);
    });
    // Zdejmowanie z grupy w tym samym miejscu co przypisanie — inaczej trzeba
    // szukać osobnej ścieżki dla najczęstszego „cofnij".
    var zdejmij = document.createElement('option');
    zdejmij.value = '-';
    zdejmij.textContent = '— zdejmij z grupy roboczej —';
    sel.appendChild(zdejmij);
  } catch (e: any) {
    // Pasek działa dalej bez grup — reszta akcji masowych nie ma z tym nic wspólnego.
    try { console.warn('[bulk] nie udało się pobrać grup roboczych:', e); } catch (_) {}
  }
}

async function _przeniesDoGrupyZPaska(): Promise<void> {
  var sel: any = document.getElementById('bulk-group-select-inline');
  var wartosc = String((sel && sel.value) || '');
  if (!wartosc) { showToast('Wybierz grupę z listy', 'error'); return; }
  var ids = Array.from((state as any).ordersSelectedIds || []) as string[];
  if (!ids.length) { showToast('Nie zaznaczono zamówień', 'error'); return; }
  var groupId = wartosc === '-' ? null : parseInt(wartosc, 10);
  var nazwa = (sel && sel.options[sel.selectedIndex]) ? sel.options[sel.selectedIndex].text : '';
  var btn: any = document.getElementById('bulk-apply-group');
  if (btn) btn.disabled = true;
  try {
    await _przypiszGrupeZaznaczonym(ids, groupId, nazwa);
    if (sel) sel.value = '';
  } catch (e: any) {
    showToast('Nie udało się przenieść: ' + ((e && e.message) || e), 'error');
  } finally {
    if (btn) btn.disabled = false;
  }
}


async function _openBulkMoveGroupModal() {
  var ids = Array.from((state as any).ordersSelectedIds || []) as string[];
  if (!ids.length) { showToast('Nie zaznaczono żadnego zamówienia', 'warning'); return; }
  var grupy: any[] = [];
  try {
    grupy = await _grupyRobocze();
  } catch (e: any) {
    showToast('Nie udało się pobrać grup: ' + ((e && e.message) || e), 'error');
    return;
  }
  if (!grupy.length) {
    showToast('Nie masz żadnej grupy roboczej — załóż ją w Ustawienia → Zamówienia i statusy („+ Nowa grupa" → typ Robocza)', 'info');
    return;
  }
  openModal(
    'Przenieś do grupy roboczej — ' + ids.length + ' zamówień',
    '<select id="bulk-group-select" class="form-select" style="width:100%">' +
      grupy.map(function (g: any) {
        return '<option value="' + String(g.id) + '">' + escapeHtml(String(g.name || '')) + '</option>';
      }).join('') +
      '<option value="">— zdejmij z grupy roboczej —</option>' +
    '</select>' +
    '<div style="margin-top:8px;font-size:11.5px;color:var(--color-text-muted);line-height:1.5">' +
    'Zamówienie może być w <b>jednej</b> grupie roboczej — przeniesienie zabiera z poprzedniej. ' +
    'Dopóki tu siedzi, nie liczy się w grupach automatycznych; po zdjęciu samo wraca tam, ' +
    'gdzie wynika ze statusu i płatności.</div>',
    '<button class="btn-secondary" id="bulk-group-cancel">Anuluj</button>' +
    '<button class="btn-primary" id="bulk-group-save">Przenieś (' + ids.length + ')</button>'
  );
  var cancelBtn = document.getElementById('bulk-group-cancel');
  if (cancelBtn) cancelBtn.addEventListener('click', function () { closeModal(); });
  var saveBtn: any = document.getElementById('bulk-group-save');
  if (saveBtn) saveBtn.addEventListener('click', async function () {
    var sel: any = document.getElementById('bulk-group-select');
    var wartosc = String((sel && sel.value) || '');
    var groupId = wartosc ? parseInt(wartosc, 10) : null;
    var nazwa = (sel && sel.options[sel.selectedIndex]) ? sel.options[sel.selectedIndex].text : '';
    saveBtn.disabled = true;
    try {
      // Jeden zapis dla okna i dla paska — inaczej dwie ścieżki rozjeżdżają się
      // przy pierwszej zmianie (raporty, czyszczenie zaznaczenia, odświeżenie).
      await _przypiszGrupeZaznaczonym(ids, groupId, nazwa);
      closeModal();
    } catch (e: any) {
      showToast('Nie udało się przenieść: ' + ((e && e.message) || e), 'error');
      saveBtn.disabled = false;
    }
  });
}

// ── Bulk wiadomość (etap 2) — ręczny wybór kanału wg macierzy z preflight ──

function _authHeaders(): Record<string, string> {
  var token = (_api() && _api().getAuthToken && _api().getAuthToken())
    || localStorage.getItem('sellbase_token') || localStorage.getItem('orderflow_token') || '';
  return { 'Authorization': 'Bearer ' + token, 'Content-Type': 'application/json' };
}

// Pola wstawiane do treści: przyjazna etykieta → klucz placeholdera (składnia
// postpurchase: {placeholder}, renderowane per zamówienie po stronie backendu).
var _MSG_PLACEHOLDERS: Array<{ label: string; key: string }> = [
  { label: 'imię', key: 'first_name' },
  { label: 'kupujący', key: 'buyer_name' },
  { label: 'nr zamówienia', key: 'order_number' },
  { label: 'przewoźnik', key: 'carrier_name' },
  { label: 'nr śledzenia', key: 'tracking_number' },
  { label: 'link śledzenia', key: 'tracking_url' },
];

async function _openBulkMessageModal() {
  var ids = _selectedIds();
  if (!ids.length) { showToast('Nie zaznaczono żadnego zamówienia', 'warning'); return; }
  var msgBtn: any = document.getElementById('bulk-send-message');
  if (msgBtn) msgBtn.disabled = true;
  var pf: any = null;
  try {
    var resp = await fetch('/api/orders/bulk/message-preflight', {
      method: 'POST', headers: _authHeaders(), body: JSON.stringify({ order_ids: ids }),
    });
    pf = await resp.json();
    if (!resp.ok) throw new Error((pf && pf.detail) || 'Błąd sprawdzania kanałów');
  } catch (e: any) {
    if (msgBtn) msgBtn.disabled = false;
    showToast('Nie udało się sprawdzić kanałów: ' + ((e && e.message) || e), 'error');
    return;
  }
  if (msgBtn) msgBtn.disabled = false;

  var total = pf.total || ids.length;
  var chAllegro = (pf.channels && pf.channels.allegro) || { available: 0 };
  var chEmail = (pf.channels && pf.channels.email) || { available: 0 };
  var replyOnly = pf.reply_only || [];

  function _covBadge(n: number) {
    var full = n === total;
    return '<span style="margin-left:auto;font-size:11px;font-weight:600;color:' +
      (n === 0 ? 'var(--color-text-muted)' : full ? 'var(--color-success,#12b76a)' : 'var(--color-warning-500,#f79009)') +
      '">obejmie ' + n + ' z ' + total + '</span>';
  }
  function _chanRow(key: string, name: string, desc: string, n: number, disabled: boolean, extra?: string) {
    return '<label class="bulk-msg-chan" data-chan="' + key + '" style="display:flex;align-items:center;gap:10px;padding:10px 12px;' +
      'border:1px solid var(--color-border);border-radius:10px;cursor:' + (disabled ? 'not-allowed' : 'pointer') + ';' +
      (disabled ? 'opacity:.55' : '') + '">' +
      '<input type="radio" name="bulk-msg-chan" value="' + key + '"' + (disabled ? ' disabled' : '') + '>' +
      '<div style="min-width:0"><div style="font-weight:600;font-size:12.5px">' + name + '</div>' +
      '<div style="font-size:11px;color:var(--color-text-muted)">' + desc + '</div></div>' +
      (extra || _covBadge(n)) + '</label>';
  }

  var chansHtml = '<div style="display:flex;flex-direction:column;gap:7px">';
  chansHtml += _chanRow('allegro', 'Wiadomość Allegro', 'nowy wątek z kupującym w Centrum Wiadomości (w kontekście zamówienia)', chAllegro.available, chAllegro.available === 0);
  chansHtml += _chanRow('email', 'E-mail', chEmail.configured ? 'na adres kupującego z zamówienia' : escapeHtml(chEmail.reason || 'wysyłka e-mail nieskonfigurowana'), chEmail.available, chEmail.available === 0);
  replyOnly.forEach(function (ro: any) {
    chansHtml += _chanRow(ro.key, 'Wiadomość ' + (ro.key === 'ebay' ? 'eBay' : ro.key === 'prestashop' ? 'PrestaShop' : 'TikTok'),
      escapeHtml(ro.reason || ''), 0, true, '<span style="margin-left:auto;font-size:10px;color:var(--color-text-muted)">tylko odpowiedzi</span>');
  });
  chansHtml += _chanRow('sms', 'SMS', 'wiadomość tekstowa na telefon kupującego', 0, true,
    '<span style="margin-left:auto;font-size:10px;background:var(--color-surface-3,#222f44);border-radius:6px;padding:2px 8px;color:var(--color-text-muted)">wkrótce</span>');
  chansHtml += '</div>';

  var phsHtml = '<div style="display:flex;gap:6px;flex-wrap:wrap;margin:10px 0 6px">';
  _MSG_PLACEHOLDERS.forEach(function (p) {
    phsHtml += '<button type="button" class="bulk-msg-ph" data-key="' + p.key + '" style="font-size:11px;background:var(--color-surface-3,#222f44);' +
      'border:1px solid var(--color-border-strong,#2e3d56);border-radius:6px;padding:3px 8px;color:var(--color-primary);cursor:pointer">' + p.label + '</button>';
  });
  phsHtml += '</div>';

  openModal(
    'Wyślij wiadomość — ' + total + ' zamówień',
    '<div style="font-size:11px;font-weight:600;color:var(--color-text-muted);text-transform:uppercase;letter-spacing:.04em;margin-bottom:8px">Kanał wysyłki (wybór ręczny)</div>' +
    chansHtml +
    '<div id="bulk-msg-skip-warn" style="display:none;margin-top:10px;font-size:11px;color:var(--color-warning-500,#f79009);background:var(--color-warning-bg,rgba(247,144,9,.15));border-radius:8px;padding:7px 10px"></div>' +
    '<div id="bulk-msg-subject-row" style="display:none;margin-top:10px"><input type="text" id="bulk-msg-subject" class="form-input" style="width:100%" placeholder="Temat (puste = domyślny z nr zamówienia)"></div>' +
    phsHtml +
    '<textarea id="bulk-msg-text" rows="5" class="form-input" style="width:100%;resize:vertical" placeholder="Treść wiadomości… kliknij pole powyżej, aby wstawić np. {order_number}"></textarea>' +
    '<div style="margin-top:8px;font-size:11.5px;color:var(--color-text-muted);line-height:1.5">Pola w nawiasach {…} podstawią się osobno dla każdego zamówienia. Wysyłka zapisze się w osi czasu zamówienia. Limit: 2000 znaków (Allegro).</div>',
    '<button class="btn-secondary" id="bulk-msg-cancel">Anuluj</button>' +
    '<button class="btn-primary" id="bulk-msg-send" disabled>Wyślij</button>',
    true
  );

  var chosen = '';
  var sendBtn: any = document.getElementById('bulk-msg-send');
  var skipWarn = document.getElementById('bulk-msg-skip-warn');
  var subjRow = document.getElementById('bulk-msg-subject-row');

  function _eligibleIds(chan: string): string[] {
    return (pf.orders || []).filter(function (o: any) {
      return chan === 'allegro' ? o.allegro_ok : chan === 'email' ? o.email_ok : false;
    }).map(function (o: any) { return o.id; });
  }
  function _refreshChoice() {
    var n = chosen ? _eligibleIds(chosen).length : 0;
    if (sendBtn) {
      sendBtn.disabled = !chosen || n === 0;
      sendBtn.textContent = chosen ? ('Wyślij przez ' + (chosen === 'allegro' ? 'Allegro' : 'e-mail') + ' (' + n + ')') : 'Wyślij';
    }
    if (subjRow) (subjRow as any).style.display = chosen === 'email' ? '' : 'none';
    if (skipWarn) {
      var skipped = (pf.orders || []).filter(function (o: any) {
        return chosen === 'allegro' ? !o.allegro_ok : chosen === 'email' ? !o.email_ok : false;
      });
      if (chosen && skipped.length) {
        skipWarn.innerHTML = '⚠ Tym kanałem nie zostanie obsłużone: ' + skipped.map(function (o: any) {
          return '<b>' + escapeHtml(o.label || o.id) + '</b>' +
            ' (' + escapeHtml(o.allegro_reason || o.email_reason || o.error || 'niedostępne') + ')';
        }).join(', ') + ' — wyślij do nich osobno innym kanałem.';
        (skipWarn as any).style.display = '';
      } else {
        (skipWarn as any).style.display = 'none';
      }
    }
    document.querySelectorAll('.bulk-msg-chan').forEach(function (el: any) {
      el.style.borderColor = el.dataset.chan === chosen ? 'var(--color-primary)' : 'var(--color-border)';
      el.style.background = el.dataset.chan === chosen ? 'var(--color-primary-highlight,rgba(70,95,255,.12))' : '';
    });
  }
  document.querySelectorAll('.bulk-msg-chan input[type=radio]').forEach(function (r: any) {
    r.addEventListener('change', function () { chosen = r.value; _refreshChoice(); });
  });
  document.querySelectorAll('.bulk-msg-ph').forEach(function (b: any) {
    b.addEventListener('click', function () {
      var ta: any = document.getElementById('bulk-msg-text');
      if (!ta) return;
      var ins = '{' + b.dataset.key + '}';
      var s = ta.selectionStart || 0; var e = ta.selectionEnd || 0;
      ta.value = ta.value.slice(0, s) + ins + ta.value.slice(e);
      ta.focus(); ta.selectionStart = ta.selectionEnd = s + ins.length;
    });
  });
  var cancelBtn = document.getElementById('bulk-msg-cancel');
  if (cancelBtn) cancelBtn.addEventListener('click', function () { closeModal(); });
  if (sendBtn) sendBtn.addEventListener('click', async function () {
    var ta: any = document.getElementById('bulk-msg-text');
    var text = (ta && ta.value || '').trim();
    if (!text) { showToast('Wpisz treść wiadomości', 'warning'); if (ta) ta.focus(); return; }
    if (text.length > 2000 && chosen === 'allegro') { showToast('Limit Allegro to 2000 znaków (masz ' + text.length + ')', 'warning'); return; }
    var subjEl: any = document.getElementById('bulk-msg-subject');
    var subject = (subjEl && subjEl.value || '').trim();
    var sendIds = _eligibleIds(chosen);
    if (!sendIds.length) return;
    var byId: any = {};
    (pf.orders || []).forEach(function (o: any) { byId[o.id] = o; });
    sendBtn.disabled = true;
    // INT-012 (audyt Codex): wynik nieustalony (5xx / brak odpowiedzi) ≠ pewny błąd — wiadomość mogła wyjść.
    var nieustalone: any = {};
    var results = await _runBulkOverOrders(sendIds, async function (orderId) {
      var r: any;
      try {
        r = await fetch('/api/orders/' + encodeURIComponent(orderId) + '/message', {
          method: 'POST', headers: _authHeaders(),
          body: JSON.stringify({ channel: chosen, text: text, subject: subject }),
        });
      } catch (_netErr) {
        nieustalone[orderId] = true;
        throw new Error('wynik nieustalony (brak odpowiedzi serwera) — sprawdź zamówienie, zanim wyślesz ponownie');
      }
      var j = await r.json().catch(function () { return {}; });
      if (!r.ok) {
        if (r.status >= 500) {
          nieustalone[orderId] = true;
          throw new Error('wynik nieustalony (HTTP ' + r.status + ') — sprawdź zamówienie, zanim wyślesz ponownie');
        }
        throw new Error((j && j.detail) || ('HTTP ' + r.status));
      }
      return (byId[orderId] && byId[orderId].label) || orderId;
    }, { progressBtn: sendBtn, progressLabel: 'Wysyłam', concurrency: 2 });
    // Pominięte (kanał niedostępny) dopisujemy do raportu jako informacja.
    (pf.orders || []).forEach(function (o: any) {
      if (sendIds.indexOf(o.id) === -1) {
        results.push({ id: o.id, label: o.label || o.id, ok: false,
          error: 'pominięte — ' + (o.allegro_reason || o.email_reason || o.error || 'kanał niedostępny') });
      }
    });
    closeModal();
    _showBulkReport('Wiadomość — kanał: ' + (chosen === 'allegro' ? 'Allegro' : 'e-mail'), results, {
      note: 'Każda wysłana wiadomość została zapisana w osi czasu zamówienia.',
    });
    // Review 2026-07-24: odznaczamy TYLKO obsłużone zamówienia — pominięte (kanał
    // niedostępny) zostają zaznaczone, żeby dało się je od razu wysłać innym kanałem.
    // 2026-10-11 INT-012 (audyt Codex): wcześniej odznaczano wyłącznie, gdy WSZYSTKIE się udały — przy częściowym
    // sukcesie ponowienie z zachowanym zaznaczeniem wysyłało udane drugi raz. Teraz odznaczamy każde udane i każde
    // nieustalone (ponowienie mogłoby zdublować); zaznaczone zostają tylko pewne odmowy i pominięte.
    var odznacz = results.filter(function (r) {
      return sendIds.indexOf(r.id) !== -1 && (r.ok || nieustalone[r.id]);
    }).map(function (r) { return r.id; });
    if (odznacz.length) {
      try {
        odznacz.forEach(function (id) { (state as any).ordersSelectedIds.delete(id); });
        _updateBulkBar();
        // S-139 (audyt 07.10): store i tak jest niezmieniony (wiadomość nie zmienia danych
        // zamówienia) — renderOrdersView() tu tylko odznaczał checkboxy. Punktowo.
        odznacz.forEach(function (id) {
          var esc = ((window as any).CSS && (window as any).CSS.escape) ? (window as any).CSS.escape(String(id)) : String(id);
          document.querySelectorAll('[data-order-id="' + esc + '"].order-card, [data-order-id="' + esc + '"].order-row')
            .forEach(function (row) {
              row.classList.remove('selected');
              var cb = row.querySelector('.order-card-check, .order-row-check') as HTMLInputElement | null;
              if (cb) cb.checked = false;
            });
        });
      } catch (_) {}
    }
  });
}

// ── Grupy wysyłkowe („połącz zamówienia") — etap 3 ──────────────────────────
// Zasada: oryginalne zamówienia zostają; grupa = nadrzędny byt (jedna przesyłka,
// tracking propagowany wszystkim członkom). Łączenie TYLKO jedno konto + jeden
// kupujący (decyzja usera 2026-07-24).

function showConfirm(this: any, ...args: any[]) { return ((((window as any).__sbExports && (window as any).__sbExports.showConfirm) || (window as any).showConfirm) as any).apply(this, args); }

// Licznik/stan „Połącz" na pasku. Przycisk jest ZAWSZE widoczny (odkrywalność —
// user nie znajdował ukrytego przycisku, 2026-07-24): przy <2 zaznaczonych wyszarzony
// z podpowiedzią; przy ≥2 zawsze klikalny — pełna walidacja i powody odrzucenia są
// w modalu (preflight server-side). Licznik (n) to klient-side przybliżenie po
// (source, buyer) ze store'a.
export function _updateBulkMergeCount() {
  var btn: any = document.getElementById('bulk-merge-orders');
  if (!btn) return;
  var ids = _selectedIds();
  var buckets: any = {};
  ids.forEach(function (id) {
    var o = _data() && _data().getOrderById ? _data().getOrderById(id) : null;
    if (!o) return;
    var src = (o.source && o.source.key) || o.source || '';
    var buyer = (o.buyer_login || o.buyer_email || o.customerEmail || '').toLowerCase();
    if (!buyer) return;
    var k = src + '|' + buyer;
    buckets[k] = (buckets[k] || 0) + 1;
  });
  var best = 0;
  Object.keys(buckets).forEach(function (k) { if (buckets[k] > best) best = buckets[k]; });
  btn.style.display = '';
  // 2026-07-24 v2 (feedback usera): warunki niespełnione = przycisk WYSZARZONY bez
  // możliwości kliku; tooltip (title) tłumaczy, co trzeba spełnić. Aktywny tylko gdy
  // wśród zaznaczonych są ≥2 zamówienia tego samego kupującego z tego samego źródła.
  var mergeable = best >= 2;
  btn.disabled = !mergeable;
  btn.title = ids.length < 2
    ? 'Zaznacz co najmniej 2 zamówienia tego samego kupującego, aby połączyć je w jedną przesyłkę'
    : (mergeable
        ? 'Połącz zamówienia tego samego kupującego w jedną przesyłkę'
        : 'Nie można połączyć: łączyć można tylko zamówienia TEGO SAMEGO kupującego z tego samego konta marketplace');
  var cntEl = document.getElementById('bulk-merge-count');
  if (cntEl) cntEl.textContent = mergeable ? '(' + best + ')' : '';
}

async function _openBulkMergeModal() {
  var ids = _selectedIds();
  if (ids.length < 2) { showToast('Zaznacz co najmniej 2 zamówienia', 'warning'); return; }
  var pf: any = null;
  try {
    var resp = await fetch('/api/order-groups/preflight', {
      method: 'POST', headers: _authHeaders(), body: JSON.stringify({ order_ids: ids }),
    });
    pf = await resp.json();
    if (!resp.ok) throw new Error((pf && pf.detail) || 'Błąd sprawdzania');
  } catch (e: any) {
    showToast('Nie można sprawdzić łączenia: ' + ((e && e.message) || e), 'error');
    return;
  }
  var eligible = (pf.orders || []).filter(function (o: any) { return o.eligible; });
  var excluded = (pf.orders || []).filter(function (o: any) { return !o.eligible; });
  if (eligible.length < 2) {
    // Zamiast toastu — modal informacyjny z POWODEM per zamówienie (2026-07-24:
    // user nie wiedział, czemu nie może połączyć; teraz widzi to czarno na białym).
    var whyRows = '';
    (pf.orders || []).forEach(function (o: any) {
      whyRows += '<div style="display:flex;align-items:center;gap:10px;border:1px solid var(--color-border);border-radius:10px;padding:9px 12px;margin-bottom:7px">' +
        '<b>' + escapeHtml(o.label || o.id) + '</b>' +
        '<span style="margin-left:auto;font-size:10.5px;color:var(--color-warning-500,#f79009)">' +
        escapeHtml(o.reason || (o.eligible ? 'OK — ale brak drugiego pasującego zamówienia' : 'niedostępne')) + '</span></div>';
    });
    openModal(
      'Nie można połączyć zaznaczonych zamówień',
      '<div style="font-size:12px;color:var(--color-text-muted);margin-bottom:10px;line-height:1.6">' +
      'Łączyć można <b>co najmniej 2 zamówienia tego samego kupującego z tego samego konta marketplace</b>, ' +
      'bez aktywnych przesyłek. Stan zaznaczonych:</div>' + whyRows,
      '<button class="btn-primary" id="bulk-merge-why-close">OK</button>'
    );
    var wc = document.getElementById('bulk-merge-why-close');
    if (wc) wc.addEventListener('click', function () { closeModal(); });
    return;
  }
  var rows = '';
  eligible.forEach(function (o: any, i: number) {
    rows += '<label style="display:flex;align-items:center;gap:10px;border:1px solid var(--color-border);border-radius:10px;padding:9px 12px;margin-bottom:7px;cursor:pointer">' +
      '<input type="radio" name="bulk-merge-primary" value="' + escapeHtml(o.id) + '"' + (i === 0 ? ' checked' : '') + '>' +
      '<b>' + escapeHtml(o.label || o.id) + '</b>' +
      '<span style="color:var(--color-text-muted);font-size:11px">' + escapeHtml(formatLocalDateTimePL(o.bought_at)) + (o.amount ? ' · ' + escapeHtml(String(o.amount)) : '') + '</span>' +
      '</label>';
  });
  excluded.forEach(function (o: any) {
    rows += '<div style="display:flex;align-items:center;gap:10px;border:1px solid var(--color-border);border-radius:10px;padding:9px 12px;margin-bottom:7px;opacity:.5">' +
      '<b>' + escapeHtml(o.label || o.id) + '</b>' +
      '<span style="margin-left:auto;font-size:10.5px;color:var(--color-error-500,#f04438)">' + escapeHtml(o.reason || 'niedostępne') + '</span></div>';
  });
  var addrHtml = pf.address_match
    ? '<div style="font-size:12px;color:var(--color-success,#12b76a);margin:8px 0">✔ Adresy dostawy zgodne' +
      (eligible[0] && eligible[0].address_text ? ': ' + escapeHtml(eligible[0].address_text) : '') + '</div>'
    : '<div style="font-size:12px;color:var(--color-warning-500,#f79009);margin:8px 0">⚠ Adresy dostawy RÓŻNIĄ się między zamówieniami — etykieta powstanie z adresu zamówienia wiodącego.<br>' +
      '<label style="display:flex;gap:6px;align-items:center;margin-top:6px;cursor:pointer"><input type="checkbox" id="bulk-merge-addr-ok"> Rozumiem, łącz mimo różnych adresów</label></div>';
  openModal(
    'Połącz zamówienia w jedną przesyłkę',
    '<div style="font-size:11px;font-weight:600;color:var(--color-text-muted);text-transform:uppercase;letter-spacing:.04em;margin-bottom:8px">Do połączenia (' + eligible.length + ') — wybierz zamówienie wiodące</div>' +
    rows + addrHtml +
    '<div style="background:var(--color-surface-2);border:1px solid var(--color-border);border-radius:10px;padding:11px 13px;font-size:12px;line-height:1.7;margin-top:6px">' +
    '<b>Co się stanie:</b><br>' +
    '• powstanie przesyłka łączona (' + eligible.length + ' zamówień, kupujący ' + escapeHtml(pf.buyer || '') + ')<br>' +
    '• jedna etykieta — numer śledzenia trafi do WSZYSTKICH zamówień grupy<br>' +
    '• faktury/paragony pozostają osobno per zamówienie<br>' +
    '• grupę można rozłączyć, dopóki nie ma aktywnej przesyłki</div>',
    '<button class="btn-secondary" id="bulk-merge-cancel">Anuluj</button>' +
    '<button class="btn-primary" id="bulk-merge-do">Połącz (' + eligible.length + ')</button>'
  );
  var cancelBtn = document.getElementById('bulk-merge-cancel');
  if (cancelBtn) cancelBtn.addEventListener('click', function () { closeModal(); });
  var doBtn: any = document.getElementById('bulk-merge-do');
  if (doBtn) doBtn.addEventListener('click', async function () {
    if (!pf.address_match) {
      var chk: any = document.getElementById('bulk-merge-addr-ok');
      if (!chk || !chk.checked) { showToast('Potwierdź zgodę na różne adresy', 'warning'); return; }
    }
    var prim: any = document.querySelector('input[name="bulk-merge-primary"]:checked');
    doBtn.disabled = true;
    try {
      var r = await fetch('/api/order-groups', {
        method: 'POST', headers: _authHeaders(),
        body: JSON.stringify({
          order_ids: eligible.map(function (o: any) { return o.id; }),
          primary_order_id: (prim && prim.value) || '',
          allow_address_mismatch: !pf.address_match,
        }),
      });
      var j = await r.json().catch(function () { return {}; });
      if (!r.ok) throw new Error((j && j.detail) || ('HTTP ' + r.status));
      closeModal();
      showToast('Połączono ' + (j.member_count || eligible.length) + ' zamówień w grupę #G-' + j.group_id, 'success');
      try { (state as any).ordersSelectedIds.clear(); } catch (_) {}
      try { _updateBulkBar(); } catch (_) {}
      try { renderOrdersView(); } catch (_) {}
    } catch (e: any) {
      doBtn.disabled = false;
      showToast('Nie udało się połączyć: ' + ((e && e.message) || e), 'error');
    }
  });
}

// ── Badge „w grupie" na liście + drawer grupy ──
var _groupBadgeTimer: any = null;

export function _refreshGroupBadges() {
  if (_groupBadgeTimer) clearTimeout(_groupBadgeTimer);
  // Debounce + retry — render listy bywa async (fetch danych po pierwszym paint).
  _groupBadgeTimer = setTimeout(_doRefreshGroupBadges, 600);
}

async function _doRefreshGroupBadges() {
  var container = document.getElementById('orders-list-container') || document.body;
  // Review 2026-07-24 (HIGH): domyślny widok tabeli to VIRTUAL — wiersze są <div
  // class="order-row">, nie <tr>. Selektor bez prefiksu tr łapie wszystkie 3 renderery
  // (karty, virtual divy, legacy <tr> przy ?virtual=0).
  var els = container.querySelectorAll('.order-card[data-order-id], .order-row[data-order-id]');
  if (!els.length) return;
  var ids: string[] = [];
  els.forEach(function (el: any) { var id = el.getAttribute('data-order-id'); if (id && ids.indexOf(id) === -1) ids.push(id); });
  var map: any = {};
  try {
    var r = await fetch('/api/order-groups/lookup?order_ids=' + encodeURIComponent(ids.join(',')), { headers: _authHeaders() });
    var j = await r.json();
    if (r.ok) map = (j && j.groups) || {};
  } catch (_) { return; }
  (window as any).__sbOrderGroups = map;
  els.forEach(function (el: any) {
    var id = el.getAttribute('data-order-id');
    var g = map[id];
    var existing = el.querySelector('.order-group-badge');
    if (!g) { if (existing) existing.remove(); return; }
    if (existing) return;
    var badge = document.createElement('span');
    badge.className = 'order-group-badge';
    badge.setAttribute('data-group-id', String(g.group_id));
    badge.style.cssText = 'display:inline-flex;align-items:center;gap:4px;font-size:10px;font-weight:600;padding:2px 8px;border-radius:6px;background:var(--color-primary-highlight,rgba(70,95,255,.12));color:var(--color-primary);border:1px dashed var(--color-primary);cursor:pointer;margin-left:6px;vertical-align:middle';
    badge.textContent = '⧉ grupa #G-' + g.group_id + ' (' + g.member_count + ')';
    badge.title = 'Przesyłka łączona — kliknij, aby otworzyć grupę';
    badge.addEventListener('click', function (ev) {
      ev.stopPropagation(); ev.preventDefault();
      _openGroupDrawer(g.group_id);
    });
    // Karta: do nagłówka; tabela: do 2. komórki (numer zamówienia).
    var host = el.querySelector('.order-card-head') || el.querySelector('td:nth-child(2)') || el;
    host.appendChild(badge);
  });
}

async function _openGroupDrawer(groupId: number) {
  var g: any = null;
  try {
    var r = await fetch('/api/order-groups/' + groupId, { headers: _authHeaders() });
    g = await r.json();
    if (!r.ok) throw new Error((g && g.detail) || 'Błąd pobierania grupy');
  } catch (e: any) {
    showToast('Nie udało się otworzyć grupy: ' + ((e && e.message) || e), 'error');
    return;
  }
  var memberIds: string[] = (g.members || []).map(function (m: any) { return String(m.order_id); });
  // Szczegóły członków (produkty) — istniejące API per zamówienie.
  var details: any = {};
  await Promise.all(memberIds.map(async function (oid) {
    try { details[oid] = await _api().getOrder(oid); } catch (_) { details[oid] = null; }
  }));

  var palette = ['var(--color-primary,#465fff)', '#8b5cf6', '#0ba5ec', '#f79009', '#12b76a', '#f04438'];
  var totalItems = 0;
  var aggregate: any[] = [];
  var sectionsHtml = '';
  memberIds.forEach(function (oid, idx) {
    var d = details[oid] || {};
    var items: any[] = [];
    try { items = JSON.parse(d.items_json || '[]'); } catch (_) {}
    if (!items.length && Array.isArray(d.items)) items = d.items;
    var isPrimary = String(g.primary_order_id) === oid;
    var itemsHtml = '';
    items.forEach(function (it: any) {
      var off = it.offer || {};
      var name = off.name || it.name || 'Pozycja';
      var qty = parseInt(String(it.quantity || (it.offer && it.offer.quantity) || 1), 10) || 1;
      totalItems += qty;
      aggregate.push({ name: name, qty: qty, from: oid });
      itemsHtml += '<div style="display:flex;gap:10px;align-items:center;padding:4px 0;font-size:12px">' +
        '<span style="flex:1;min-width:0;overflow:hidden;text-overflow:ellipsis">' + escapeHtml(name) + '</span>' +
        '<span style="color:var(--color-text-muted);flex-shrink:0">' + qty + ' szt.</span></div>';
    });
    sectionsHtml += '<div style="border:1px solid var(--color-border);border-left:3px solid ' + palette[idx % palette.length] + ';border-radius:10px;margin-bottom:9px;overflow:hidden">' +
      '<div style="display:flex;align-items:center;gap:8px;padding:8px 12px;background:var(--color-surface-2);font-size:12px">' +
      '<b>' + escapeHtml(String(d.order_number || oid)) + '</b>' +
      (isPrimary ? '<span style="font-size:9px;font-weight:700;border:1px dashed var(--color-primary);color:var(--color-primary);border-radius:6px;padding:1px 6px">WIODĄCE</span>' : '') +
      '<span style="color:var(--color-text-muted)">' + escapeHtml(formatLocalDateTimePL(d.bought_at || d.date)) + '</span>' +
      '<a href="javascript:void(0)" data-open-order="' + escapeHtml(oid) + '" style="margin-left:auto;color:var(--color-primary);font-size:11px;text-decoration:none">otwórz ↗</a></div>' +
      '<div style="padding:7px 12px">' + (itemsHtml || '<span style="font-size:11px;color:var(--color-text-muted)">brak pozycji</span>') + '</div></div>';
  });
  var aggHtml = '';
  aggregate.forEach(function (a) {
    aggHtml += '<div style="display:flex;justify-content:space-between;gap:10px;font-size:12px;padding:3px 0">' +
      '<span><b>' + a.qty + '×</b> ' + escapeHtml(a.name) + '</span>' +
      '<span style="color:var(--color-text-muted);flex-shrink:0">z ' + escapeHtml(String((details[a.from] || {}).order_number || a.from)) + '</span></div>';
  });
  var shipHtml = '';
  if (g.shipment) {
    shipHtml = '<div style="margin-top:10px;font-size:12px;color:var(--color-text-muted)">Przesyłka: ' + escapeHtml(String(g.shipment.provider || '')) +
      ' · status ' + escapeHtml(String(g.shipment.status || '')) +
      (g.shipment.tracking_number ? ' · tracking <b style="color:var(--color-text)">' + escapeHtml(String(g.shipment.tracking_number)) + '</b> (propagowany do wszystkich zamówień)' : '') + '</div>';
  }
  openModal(
    '📦 Przesyłka łączona #G-' + g.id,
    '<div style="font-size:12px;color:var(--color-text-muted);margin-bottom:12px">' +
    'kupujący <b style="color:var(--color-text)">' + escapeHtml(g.buyer_key || '') + '</b>' +
    ' · utworzył ' + escapeHtml(g.created_by || '') + ' ' + escapeHtml(formatLocalDateTimePL(g.created_at)) +
    ' · składa się z <b style="color:var(--color-primary)">' + memberIds.length + ' zamówień</b></div>' +
    '<div style="font-size:11px;font-weight:600;color:var(--color-text-muted);text-transform:uppercase;letter-spacing:.04em;margin-bottom:8px">Skład grupy</div>' +
    sectionsHtml +
    '<div style="font-size:11px;font-weight:600;color:var(--color-text-muted);text-transform:uppercase;letter-spacing:.04em;margin:12px 0 6px">Do spakowania łącznie (' + totalItems + ' szt.)</div>' +
    '<div style="background:var(--color-surface-2);border:1px solid var(--color-border);border-radius:10px;padding:9px 13px">' + aggHtml + '</div>' +
    shipHtml +
    '<div style="margin-top:10px;font-size:11px;color:var(--color-text-muted)">Faktury/paragony pozostają osobno per zamówienie. Zamówienia zachowują własne osie czasu i sync z marketplace.</div>',
    '<button class="btn-secondary" id="group-dissolve" style="margin-right:auto;color:var(--color-error-500,#f04438)">Rozłącz grupę</button>' +
    '<button class="btn-secondary" id="group-close">Zamknij</button>' +
    '<button class="btn-primary" id="group-label">🏷 Generuj etykietę</button>',
    true
  );
  document.querySelectorAll('[data-open-order]').forEach(function (a: any) {
    a.addEventListener('click', function () {
      try { closeModal(); (window as any).SellBaseApp.openOrderDrawerOverlay(a.getAttribute('data-open-order')); } catch (_) {}
    });
  });
  var closeB = document.getElementById('group-close');
  if (closeB) closeB.addEventListener('click', function () { closeModal(); });
  var labelB: any = document.getElementById('group-label');
  if (labelB) labelB.addEventListener('click', async function () {
    labelB.disabled = true; labelB.textContent = 'Generuję…';
    try {
      var r = await fetch('/api/order-groups/' + g.id + '/label', { method: 'POST', headers: _authHeaders() });
      var j = await r.json().catch(function () { return {}; });
      if (!r.ok) throw new Error((j && j.detail) || ('HTTP ' + r.status));
      closeModal();
      showToast('Etykieta wygenerowana (' + (j.provider || '') + ') — tracking trafi do wszystkich zamówień grupy', 'success');
      try { renderOrdersView(); } catch (_) {}
    } catch (e: any) {
      labelB.disabled = false; labelB.textContent = '🏷 Generuj etykietę';
      showToast('Etykieta: ' + ((e && e.message) || e), 'error');
    }
  });
  var dissB = document.getElementById('group-dissolve');
  if (dissB) dissB.addEventListener('click', async function () {
    var ok = await showConfirm('Rozłączyć grupę #G-' + g.id + '?',
      'Zamówienia wrócą do osobnej obsługi. Możliwe tylko, dopóki grupa nie ma aktywnej przesyłki.',
      { okLabel: 'Rozłącz', okClass: 'danger' }).catch(function () { return false; });
    // showConfirm współdzieli globalny modal — anulowanie zamknęło drawer grupy,
    // więc otwieramy go ponownie (review 2026-07-24).
    if (!ok) { _openGroupDrawer(g.id); return; }
    try {
      var r = await fetch('/api/order-groups/' + g.id, { method: 'DELETE', headers: _authHeaders() });
      var j = await r.json().catch(function () { return {}; });
      if (!r.ok) throw new Error((j && j.detail) || ('HTTP ' + r.status));
      closeModal();
      showToast('Grupa #G-' + g.id + ' rozłączona', 'success');
      try { renderOrdersView(); } catch (_) {}
    } catch (e: any) {
      showToast('Rozłączenie: ' + ((e && e.message) || e), 'error');
    }
  });
}

// ── Bulk generowanie etykiet (etap 4) — silnik auto-etykiet per zamówienie ──
async function _bulkGenerateLabels() {
  var ids = _selectedIds();
  if (!ids.length) { showToast('Nie zaznaczono żadnego zamówienia', 'warning'); return; }
  var ok = await showConfirm(
    'Wygenerować etykiety dla ' + ids.length + ' zamówień?',
    'Etykiety powstaną wg ustawień auto-etykiet (WzA/InPost). Zamówienia z aktywną przesyłką zostaną pominięte (bez duplikatów). Zamówienie w grupie dostaje jedną wspólną etykietę.',
    { okLabel: 'Generuj (' + ids.length + ')' }
  ).catch(function () { return false; });
  if (!ok) return;
  var btn = document.getElementById('bulk-generate-labels');
  // Review 2026-07-24 (HIGH): świeży lookup grup dla ZAZNACZONYCH id — mapa z badge'y
  // bywa nieświeża/pusta (inna strona listy). Server-side guard w /generate-label
  // i tak przekierowuje członka grupy na etykietę primary — to pas i szelki.
  var groupsMap: any = {};
  try {
    var glr = await fetch('/api/order-groups/lookup?order_ids=' + encodeURIComponent(ids.join(',')), { headers: _authHeaders() });
    var glj = await glr.json();
    if (glr.ok) groupsMap = (glj && glj.groups) || {};
  } catch (_) {}
  // Jedna etykieta per grupa: wspólna OBIETNICA per group_id — kolejni członkowie
  // czekają na ten sam request; jego błąd = błąd KAŻDEGO członka (bez fałszywych ✔).
  var groupPromises: any = {};
  var results = await _runBulkOverOrders(ids, async function (orderId) {
    var order = _data().getOrderById(orderId);
    var apiId = order ? getOrderApiId(order) : orderId;
    var g = groupsMap[orderId] || groupsMap[apiId];
    var j;
    if (g && g.group_id) {
      if (!groupPromises[g.group_id]) {
        groupPromises[g.group_id] = (async function () {
          var r = await fetch('/api/order-groups/' + g.group_id + '/label', { method: 'POST', headers: _authHeaders() });
          var jj = await r.json().catch(function () { return {}; });
          if (!r.ok) throw new Error((jj && jj.detail) || ('HTTP ' + r.status));
          return jj;
        })();
      }
      j = await groupPromises[g.group_id];
      return _orderLabel(order) + ' (grupa #G-' + g.group_id + (j.reused ? ' — reuse' : '') + ')';
    }
    var r2 = await fetch('/api/orders/' + encodeURIComponent(apiId) + '/generate-label', { method: 'POST', headers: _authHeaders() });
    j = await r2.json().catch(function () { return {}; });
    if (!r2.ok) throw new Error((j && j.detail) || ('HTTP ' + r2.status));
    return _orderLabel(order) + (j.reused ? ' (istniejąca przesyłka — reuse)' : (j.provider ? ' (' + j.provider + ')' : ''))
      + (j.group_id ? ' [grupa #G-' + j.group_id + ']' : '');
  }, { progressBtn: btn, progressLabel: 'Generuję', concurrency: 2 });
  _showBulkReport('Generowanie etykiet', results, {
    note: 'Routing przewoźnika wg ustawień auto-etykiet. Tracking przesyłki grupy trafia do wszystkich jej zamówień.',
  });
  _afterBulk(results);
}

// ── „Pakuj: etykieta + druk" (2026-09-09, zgłoszenie usera) ──────────────────
/**
 * Jeden klik zamiast trzech: „Generuj etykiety" czyściło zaznaczenie, więc pakujący
 * musiał zaznaczać te same zamówienia drugi raz, żeby kliknąć „Drukuj etykiety".
 *
 * Kolejność PER ZAMÓWIENIE: etykieta (silnik auto-etykiet, `bez_zmiany_statusu: 1`)
 * → wydruk przez helper SellBase Print → dopiero potwierdzenie wydruku
 * (POST /label-printed) przestawia zamówienie na „Wysłane". Jeśli cokolwiek padnie,
 * status zostaje, a zamówienie POZOSTAJE zaznaczone — ponowny klik działa tylko na
 * nieudanych i jest bezpieczny (istniejąca przesyłka = reuse, bez drugiego zakupu
 * etykiety). Generowanie idzie po 2 naraz, wydruki po kolei (jedna kolejka — helper
 * źle znosi równoległe zlecenia). Grupa wysyłkowa: jedna etykieta, jeden wydruk,
 * potwierdzenie per członek.
 */
async function _bulkPackAndPrintLabels() {
  var ids = _selectedIds();
  if (!ids.length) { showToast('Nie zaznaczono żadnego zamówienia', 'warning'); return; }
  var w: any = window as any;
  var exp: any = w.__sbExports || {};
  var printFn: any = exp.sellbasePrintShipmentLabel || w.sellbasePrintShipmentLabel;
  if (typeof printFn !== 'function') {
    showToast('Moduł druku (SellBase Print) nie jest załadowany — odśwież stronę', 'error');
    return;
  }
  var helperOk = false;
  try {
    var st = typeof exp._sellbasePrintProbe === 'function' ? await exp._sellbasePrintProbe(true) : null;
    helperOk = !!(st && st.connected);
  } catch (_) { helperOk = false; }
  var ok = await showConfirm(
    'Spakować ' + ids.length + ' zamówień (etykieta + wydruk)?',
    (helperOk ? '' : 'UWAGA: helper drukarki (SellBase Print) nie odpowiada. Etykiety powstaną, ale bez wydruku zamówienia NIE przejdą do „Wysłane".\n\n')
      + 'Kolejność: etykieta → wydruk → status „Wysłane".\n'
      + 'Do Wysłanych trafi TYLKO zamówienie z wygenerowaną i wydrukowaną etykietą. '
      + 'Nieudane zostają zaznaczone — po usunięciu przyczyny kliknij ponownie (istniejąca etykieta nie jest kupowana drugi raz).',
    { okLabel: 'Pakuj (' + ids.length + ')' }
  ).catch(function () { return false; });
  if (!ok) return;
  var btn = document.getElementById('bulk-pack-print-labels');
  var groupsMap: any = {};
  try {
    var glr = await fetch('/api/order-groups/lookup?order_ids=' + encodeURIComponent(ids.join(',')), { headers: _authHeaders() });
    var glj = await glr.json();
    if (glr.ok) groupsMap = (glj && glj.groups) || {};
  } catch (_) {}
  var groupPromises: any = {};       // etykieta grupy — jedna obietnica per grupa
  var groupPrintPromises: any = {};  // wydruk grupy — jedna etykieta fizyczna
  var kolejkaDruku: Promise<any> = Promise.resolve();
  function _drukujPoKolei(fn: () => Promise<any>): Promise<any> {
    var p = kolejkaDruku.then(fn, fn);
    kolejkaDruku = p.then(function () { return null; }, function () { return null; });
    return p;
  }
  async function _wydrukuj(shipmentId: any): Promise<string> {
    var wynik = await _drukujPoKolei(function () {
      return printFn({ shipment_id: shipmentId, document_type: 'shipping_label', historia: 'tylko_bledy', miejsce: 'lista' });
    });
    if (!wynik || !wynik.ok) {
      throw new Error('etykieta wygenerowana, ale NIE wydrukowana (' + String((wynik && wynik.error) || 'helper offline') + ') — status bez zmiany');
    }
    return String(wynik.printer || 'drukarka');
  }
  var results = await _runBulkOverOrders(ids, async function (orderId) {
    var order = _data().getOrderById(orderId);
    var apiId = order ? getOrderApiId(order) : orderId;
    var g = groupsMap[orderId] || groupsMap[apiId];
    var j: any;
    var shipmentId: any;
    var printer: string;
    var opis = '';
    if (g && g.group_id) {
      if (!groupPromises[g.group_id]) {
        groupPromises[g.group_id] = (async function () {
          var r = await fetch('/api/order-groups/' + g.group_id + '/label', {
            method: 'POST', headers: _authHeaders(), body: JSON.stringify({ bez_zmiany_statusu: 1 }),
          });
          var jj = await r.json().catch(function () { return {}; });
          if (!r.ok) throw new Error((jj && jj.detail) || ('HTTP ' + r.status));
          return jj;
        })();
      }
      j = await groupPromises[g.group_id];
      shipmentId = j && j.shipment_id;
      if (!shipmentId) throw new Error('brak identyfikatora przesyłki grupy po wygenerowaniu etykiety');
      if (!groupPrintPromises[g.group_id]) groupPrintPromises[g.group_id] = _wydrukuj(shipmentId);
      printer = await groupPrintPromises[g.group_id];
      opis = ' (grupa #G-' + g.group_id + (j.reused ? ' — reuse' : '') + ')';
    } else {
      var r2 = await fetch('/api/orders/' + encodeURIComponent(apiId) + '/generate-label', {
        method: 'POST', headers: _authHeaders(), body: JSON.stringify({ bez_zmiany_statusu: 1 }),
      });
      j = await r2.json().catch(function () { return {}; });
      if (!r2.ok) throw new Error((j && j.detail) || ('HTTP ' + r2.status));
      shipmentId = j && j.shipment_id;
      if (!shipmentId) throw new Error('brak identyfikatora przesyłki po wygenerowaniu etykiety');
      opis = j.reused ? ' (istniejąca przesyłka — reuse)' : (j.provider ? ' (' + j.provider + ')' : '');
      printer = await _wydrukuj(shipmentId);
    }
    var r3 = await fetch('/api/orders/' + encodeURIComponent(apiId) + '/label-printed', {
      method: 'POST', headers: _authHeaders(), body: JSON.stringify({ shipment_id: shipmentId, printer: printer }),
    });
    var j3 = await r3.json().catch(function () { return {}; });
    if (!r3.ok) throw new Error('wydrukowano (' + printer + '), ale status NIE zmieniony: ' + ((j3 && j3.detail) || ('HTTP ' + r3.status)));
    // 21.10.2026 (K6-01): status idzie też na Allegro — gdy się nie udało, raport mówi to wprost.
    var ostrzezenieAllegro = (j3 && j3.allegro_sync_warning) ? ' (Allegro: ' + String(j3.allegro_sync_warning) + ')' : '';
    return _orderLabel(order) + opis + ' → ' + printer + ' → Wysłane' + ostrzezenieAllegro;
  }, { progressBtn: btn, progressLabel: 'Pakuję', concurrency: 2 });
  _showBulkReport('Pakowanie zaznaczonych (etykieta + druk)', results, {
    note: 'Do „Wysłane" trafiły tylko zamówienia z wygenerowaną I wydrukowaną etykietą. '
      + 'Nieudane zostały zaznaczone — po usunięciu przyczyny kliknij „Pakuj" ponownie (istniejąca etykieta nie jest kupowana drugi raz).',
  });
  _zostawZaznaczoneNieudane(results);
}

// Udane znikają z zaznaczenia (są już w Wysłanych), nieudane ZOSTAJĄ — sedno
// zgłoszenia: żadnego ponownego zaznaczania ręką między krokami.
function _zostawZaznaczoneNieudane(results: BulkResult[]) {
  try {
    var zbiory: any[] = [];
    try { zbiory.push($ordersSelectedIds.get()); } catch (_) {}
    try { if ((state as any).ordersSelectedIds) zbiory.push((state as any).ordersSelectedIds); } catch (_) {}
    results.forEach(function (r) {
      if (!r || !r.ok) return;
      zbiory.forEach(function (z) { try { if (z && typeof z.delete === 'function') z.delete(r.id); } catch (_) {} });
    });
  } catch (_) {}
  try { _updateBulkBar(); } catch (_) {}
  _odswiezListeZamowienPoMutacji();
}

// ── „Drukuj etykiety" (23.10.2026) ───────────────────────────────────────────
/**
 * Drukuje ISTNIEJĄCE etykiety zaznaczonych zamówień przez SellBase Print — bez generowania
 * i bez zmiany statusu (to robi „Pakuj: etykieta + druk").
 *
 * Do 23.10 przycisk szukał `order.shipments[].label_url` w wierszu listy, a /api/orders/listing
 * przesyłek nie niesie — kończył się zawsze „Brak etykiet". Nawet z adresem `window.open` nie
 * wysłałby nagłówka Authorization, a GET /api/shipments/{id}/label uwierzytelnia tylko nim (401).
 *
 * Przesyłki zamówienia jak w karcie (GET /api/shipments?order_id=), drukowane tylko te z gotową
 * etykietą (etykietaDostepna — żadnego płatnego `/buy` z przycisku druku). Po jednym zamówieniu
 * naraz: wydruki wychodzą w kolejności zaznaczenia, a helper źle znosi równoległe zlecenia.
 * Ta sama przesyłka w kilku zamówieniach (grupa wysyłkowa) = jeden wydruk. Każdy druk trafia do
 * historii zamówienia (miejsce 'lista'). Helper offline → reszta bez prób (jedno okno „pobierz").
 */
async function _bulkPrintExistingLabels() {
  var ids = _selectedIds();
  if (!ids.length) { showToast('Nie zaznaczono żadnego zamówienia', 'warning'); return; }
  var w: any = window as any;
  var exp: any = w.__sbExports || {};
  var printFn: any = exp.sellbasePrintShipmentLabel || w.sellbasePrintShipmentLabel;
  if (typeof printFn !== 'function') {
    showToast('Moduł druku (SellBase Print) nie jest załadowany — odśwież stronę', 'error');
    return;
  }
  var btn = document.getElementById('bulk-print-labels');
  var wydrukowane: Record<string, string> = {};   // shipment_id → drukarka
  var helperOffline = false;
  var results = await _runBulkOverOrders(ids, async function (orderId) {
    var order = _data().getOrderById(orderId);
    var apiId = order ? getOrderApiId(order) : _apiIdZIdentyfikatora(orderId);
    var lista: any = await _api().getOrderShipments(apiId);
    var przesylki: Shipment[] = (Array.isArray(lista) ? lista : []).filter(function (s: Shipment) {
      return String(s.direction || '') !== 'return' && !przesylkaNieudana(s) && etykietaDostepna(s);
    });
    if (!przesylki.length) throw new Error('brak gotowej etykiety — najpierw „Generuj etykiety" albo „Pakuj: etykieta + druk"');
    var drukarki: string[] = [];
    var bledy: string[] = [];
    var numery: string[] = [];
    for (var i = 0; i < przesylki.length; i++) {
      var s = przesylki[i];
      var sid = String(s.id);
      var numer = String(s.tracking_number || ('przesyłka #' + sid));
      numery.push(numer);
      if (wydrukowane[sid]) { drukarki.push(wydrukowane[sid]); continue; }
      if (helperOffline) { bledy.push(numer + ': nie drukowano — helper SellBase Print nie odpowiada'); continue; }
      var wynik = await printFn({ shipment_id: sid, document_type: 'shipping_label', miejsce: 'lista' });
      if (wynik && wynik.ok) {
        wydrukowane[sid] = String(wynik.printer || 'drukarka');
        drukarki.push(wydrukowane[sid]);
      } else {
        if (wynik && wynik.error === 'helper_offline') helperOffline = true;
        bledy.push(numer + ': ' + String((wynik && wynik.error) || 'helper offline'));
      }
    }
    if (bledy.length) {
      throw new Error((drukarki.length ? 'wydrukowano ' + drukarki.length + ' z ' + przesylki.length + '; ' : 'nie wydrukowano — ') + bledy.join('; '));
    }
    var naDrukarki = drukarki.filter(function (d, j) { return drukarki.indexOf(d) === j; }).join(', ');
    return { label: (_orderLabel(order) || apiId) + ' → ' + naDrukarki, info: numery.join(', ') };
  }, { progressBtn: btn, progressLabel: 'Drukuję', concurrency: 1 });
  var udane = results.filter(function (r) { return r && r.ok; }).length;
  if (helperOffline && !udane) {
    // Okno „pobierz SellBase Print" jest już otwarte (sellbase-print.ts) — raport by je zasłonił.
    showToast('Nie wydrukowano etykiet: helper SellBase Print nie odpowiada', 'error');
    return;
  }
  _showBulkReport('Drukowanie etykiet', results, {
    note: 'Drukowane są tylko istniejące etykiety — bez generowania i bez zmiany statusu zamówień. '
      + 'Brak etykiety? Użyj „Generuj etykiety" albo „Pakuj: etykieta + druk".',
  });
}

// ── Bind (wołane z _bindOrdersToolbarEvents w toolbar.ts) ──
/**
 * PRZYPISANIE TAGU zaznaczonym zamówieniom (2026-08-10, zgłoszenie usera).
 *
 * Idzie JEDNYM żądaniem, a nie pętlą po zamówieniach: endpoint przyjmuje listę i sam
 * rozstrzyga źródło każdego zamówienia (`resolve_order_ref`). Pętla po stronie
 * przeglądarki dawałaby N żądań i połowiczny wynik przy zerwaniu połączenia.
 *
 * Operacja jest IDEMPOTENTNA — zamówienie, które już ma ten tag, nie jest błędem.
 * Raport rozróżnia „nadano" od „już miało", bo to dwie różne informacje dla usera.
 */
async function _przypiszTagMasowo(): Promise<void> {
  var sel: any = document.getElementById('bulk-tag-select');
  var tagId = parseInt((sel && sel.value) || '0', 10);
  if (!tagId) { showToast('Wybierz tag z listy', 'error'); return; }
  var ids = Array.from((state as any).ordersSelectedIds || []) as string[];
  if (!ids.length) { showToast('Nie zaznaczono zamówień', 'error'); return; }
  var btn: any = document.getElementById('bulk-apply-tag');
  if (btn) btn.disabled = true;
  try {
    var tok = (window as any).SellBaseAPI && (window as any).SellBaseAPI.getAuthToken
      ? (window as any).SellBaseAPI.getAuthToken() : '';
    var r = await fetch('/api/order-tags/assign', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + tok },
      body: JSON.stringify({ order_ids: ids, tag_id: tagId }),
    });
    var d = await r.json();
    if (!r.ok) throw new Error((d && d.detail) || ('HTTP ' + r.status));
    var nazwa = sel.options[sel.selectedIndex] ? sel.options[sel.selectedIndex].text : '';
    var czesci = ['nadano: ' + (d.changed || 0)];
    if (d.skipped) czesci.push('już miało: ' + d.skipped);
    if (d.errors && d.errors.length) czesci.push('błędy: ' + d.errors.length);
    showToast('Tag „' + nazwa + '" — ' + czesci.join(', '), (d.errors && d.errors.length) ? 'info' : 'success');
    var ot = (window as any).__sbOrderTags;
    if (ot && typeof ot.renderujPanelTagow === 'function') void ot.renderujPanelTagow();
    if (typeof (window as any).loadAllegroOrdersIntoPanel === 'function') {
      (window as any).loadAllegroOrdersIntoPanel(true);
    }
  } catch (e: any) {
    showToast('Nie udało się przypisać tagu: ' + ((e && e.message) || ''), 'error');
  } finally {
    if (btn) btn.disabled = false;
  }
}

/** Wypełnia listę tagów w pasku akcji masowych (te same tagi, co w panelu bocznym). */
export async function _odswiezListeTagowMasowych(): Promise<void> {
  var sel: any = document.getElementById('bulk-tag-select');
  if (!sel) return;
  var ot = (window as any).__sbOrderTags;
  if (!ot || typeof ot.wczytajTagi !== 'function') return;
  var tagi = await ot.wczytajTagi();
  var biezacy = sel.value;
  sel.innerHTML = '<option value="">-- Wybierz tag --</option>' +
    (tagi || []).map(function (t: any) {
      return '<option value="' + escapeAttr(String(t.id)) + '">' + escapeHtml(String(t.name)) + '</option>';
    }).join('');
  if (biezacy) sel.value = biezacy;
}

export function _bindBulkActions() {
  var tagBtn: any = document.getElementById('bulk-apply-tag');
  if (tagBtn && !tagBtn._bound) {
    tagBtn._bound = true;
    tagBtn.addEventListener('click', function () { void _przypiszTagMasowo(); });
  }
  void _odswiezListeTagowMasowych();
  var applyBtn: any = document.getElementById('bulk-apply-status');
  if (applyBtn && !applyBtn._bound) {
    applyBtn._bound = true;
    applyBtn.addEventListener('click', function () { _applyBulkStatus(); });
  }
  var noteBtn: any = document.getElementById('bulk-add-note');
  if (noteBtn && !noteBtn._bound) {
    noteBtn._bound = true;
    noteBtn.addEventListener('click', function () { _openBulkNoteModal(); });
  }
  // 2026-08-25: grupa robocza wprost w pasku (pierwsza pozycja). Okno modalne
  // zostaje pod tą samą logiką — używa go m.in. szuflada zamówienia.
  var grpApply: any = document.getElementById('bulk-apply-group');
  if (grpApply && !grpApply._bound) {
    grpApply._bound = true;
    grpApply.addEventListener('click', function () { void _przeniesDoGrupyZPaska(); });
  }
  void _wypelnijGrupyWPasku();
  var grpBtn: any = document.getElementById('bulk-move-group');
  if (grpBtn && !grpBtn._bound) {
    grpBtn._bound = true;
    grpBtn.addEventListener('click', function () { void _openBulkMoveGroupModal(); });
  }
  var msgBtn: any = document.getElementById('bulk-send-message');
  if (msgBtn && !msgBtn._bound) {
    msgBtn._bound = true;
    msgBtn.addEventListener('click', function () { _openBulkMessageModal(); });
  }
  var mergeBtn: any = document.getElementById('bulk-merge-orders');
  if (mergeBtn && !mergeBtn._bound) {
    mergeBtn._bound = true;
    mergeBtn.addEventListener('click', function () { _openBulkMergeModal(); });
  }
  var labelsBtn: any = document.getElementById('bulk-generate-labels');
  if (labelsBtn && !labelsBtn._bound) {
    labelsBtn._bound = true;
    labelsBtn.addEventListener('click', function () { _bulkGenerateLabels(); });
  }
  var packBtn: any = document.getElementById('bulk-pack-print-labels');
  if (packBtn && !packBtn._bound) {
    packBtn._bound = true;
    packBtn.addEventListener('click', function () { void _bulkPackAndPrintLabels(); });
  }
  var printBtn: any = document.getElementById('bulk-print-labels');
  if (printBtn && !printBtn._bound) {
    printBtn._bound = true;
    printBtn.addEventListener('click', function () { void _bulkPrintExistingLabels(); });
  }
}

// ── EXPOSE (wzorzec repo: window + SellBaseApp) ──
(window as any).SellBaseApp = (window as any).SellBaseApp || {};
(window as any)._bindBulkActions = _bindBulkActions; (window as any).SellBaseApp._bindBulkActions = _bindBulkActions;
(window as any)._showBulkReport = _showBulkReport; (window as any).SellBaseApp._showBulkReport = _showBulkReport;
(window as any)._runBulkOverOrders = _runBulkOverOrders;
(window as any)._refreshGroupBadges = _refreshGroupBadges; (window as any).SellBaseApp._refreshGroupBadges = _refreshGroupBadges;
(window as any)._updateBulkMergeCount = _updateBulkMergeCount;
